import { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Tag, Loader2, Save, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

/**
 * CategoryEditorDialog — lets a BCBA/owner rename any category across their whole
 * card library in one shot. Renaming to an existing category *merges* them, which
 * is exactly what BCBAs want when they've been sloppy ("Foods" vs "food" vs "Snacks").
 */
export default function CategoryEditorDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]); // [{raw, name, count, draft}]

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/cards/categories");
      const items = (r.data?.items || []).map((it) => ({
        raw: it.raw,
        name: it.name,
        count: it.count,
        draft: it.name === "Uncategorized" ? "" : it.name,
      }));
      setRows(items);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const dirty = rows.some((r) => (r.draft || "").trim() !== (r.raw || ""));

  const reset = () => {
    setRows((prev) => prev.map((r) => ({ ...r, draft: r.raw || (r.name === "Uncategorized" ? "" : r.name) })));
  };

  const save = async () => {
    const renames = rows
      .map((r) => ({ from_name: r.raw, to_name: (r.draft || "").trim() }))
      .filter((op) => op.from_name !== op.to_name);
    if (renames.length === 0) {
      toast("Nothing to save");
      return;
    }
    setSaving(true);
    try {
      const r = await http.post("/cards/categories/rename", { renames });
      toast.success(`Updated ${r.data?.updated || 0} card${r.data?.updated === 1 ? "" : "s"}`);
      onSaved?.();
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="rounded-full h-11 px-4"
          data-testid="categories-edit-btn"
        >
          <Tag className="w-4 h-4 mr-2" /> Categories
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display">Edit categories</DialogTitle>
          <DialogDescription>
            Rename any category across your whole library. Type the same name into two rows to
            merge them. Leave a row blank to make its cards Uncategorized.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500" data-testid="categories-empty">
            No cards yet. Add cards and their categories will appear here.
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-2" data-testid="categories-list">
            {rows.map((r, i) => {
              const isDirty = (r.draft || "").trim() !== (r.raw || "");
              return (
                <div
                  key={`${r.raw}-${i}`}
                  className={`flex items-center gap-2 rounded-lg border p-2 ${
                    isDirty ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"
                  }`}
                  data-testid={`category-row-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="w-14 text-right text-xs font-bold text-slate-500 shrink-0">
                    {r.count}
                  </div>
                  <div className="flex-1">
                    <Input
                      value={r.draft}
                      onChange={(e) =>
                        setRows((prev) => prev.map((p, j) => (j === i ? { ...p, draft: e.target.value } : p)))
                      }
                      placeholder={r.name === "Uncategorized" ? "Uncategorized" : "Category name"}
                      className="h-9 text-sm"
                      data-testid={`category-input-${i}`}
                    />
                  </div>
                  {isDirty && (
                    <div className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-amber-200 text-amber-900">
                      Changed
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
          <Button
            variant="outline"
            onClick={reset}
            disabled={!dirty || saving}
            className="rounded-full"
            data-testid="categories-reset"
          >
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="rounded-full"
            data-testid="categories-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
